import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";
import { customTableStyles } from "../../assets/CustomTableStyles";
import FullScreenLoader from "../../components/FullScreenLoader";
import AskConfirmationModal from "../../components/modals/AskConfirmationModal";
import PayActivityChallanModal from "./PayActivityChallanModal";
import PaymentProofModal from "../../components/modals/PaymentProofModal";
import PupilActivityEnrollmentModal from "./PupilActivityEnrollmentModal";

function EnrolledActivities() {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");
  const centerId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [triggerRerender, setTriggerRerender] = useState(false);

  const [verifiedProofs, setVerifiedProofs] = useState({}); // New state to track verified proofs

  const [isConfimationModalOpen, setIsConfimationModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const [isConfimationPaymentModalOpen, setIsConfimationPaymentModalOpen] =
    useState(false);

  const [activityApplicationsData, setActivityApplicationsData] = useState([]);

  const [showPayActivityModal, setShowPayActivityModal] = useState(false);
  const [payActivityModalData, setPayActivityModalData] = useState(null);

  const [showPaymentProofModal, setShowPaymentProofModal] = useState(false);
  const [paymentProofModalData, setPaymentProofModalData] = useState(null);

  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [dayAndTimeSlotsData, setDayAndTimeSlotsData] = useState([]);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

  const [selectedActivityRow, setSelectedActivityRow] = useState(null);
  const [activityDetails, setActivityDetails] = useState(null);

  const [schoolClubActivitiesData, setSchoolClubActivitiesData] = useState([]);

  const getActivityApplicationsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    let userIdToPass = roleId === "9" || roleId === "10" ? userId : "0";

    try {
      const response = await get(
        `CampusActivity/GetAllActivitiesByPrentId?parentId=${userIdToPass}&centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Raw API Response:", response); // Debug: Log raw API response

      // Flatten the grouped response into a single array with group field
      // const flattenedData = response.reduce((acc, group) => {
      //   const rows = group.activites.map((item) => ({
      //     ...item,
      //     group: group.activityName,
      //     isRegistered: item.isRegistered ?? false,
      //   }));
      //   return [...acc, ...rows];
      // }, []);

      // Flatten the grouped response into a single array with group field and unique rowKey
      const flattenedData = response.reduce((acc, group, groupIndex) => {
        const rows = group.activites.map((item, itemIndex) => ({
          ...item,
          group: group.activityName,
          isRegistered: item.isRegistered ?? false,
          rowKey: `${item.applicationId}-${item.id}-${groupIndex}-${itemIndex}`, // Create unique key
        }));
        return [...acc, ...rows];
      }, []);

      console.log("Normalized Activity Applications Data:", flattenedData); // Debug: Log normalized data
      setActivityApplicationsData(flattenedData);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    getActivityApplicationsList();
  }, [triggerRerender]);

  const handleDelete = async (activityId) => {
    setSelectedRowId(activityId);
    setIsConfimationModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await post(
        `CampusActivity/RemoveActivityInvoice?id=${selectedRowId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
      } else {
        toast.error(response.message || "An error occurred while deleting.");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
    } finally {
      setIsConfimationModalOpen(false);
      setSelectedRowId(null);
    }
  };

  const handleCloseModal = () => {
    setIsConfimationModalOpen(false);
    setIsConfimationPaymentModalOpen(false);
    setSelectedRowId(null);
  };

  const handlePrintActivityChallanAction = async (rowItem) => {
    if (!rowItem?.id) {
      alert("Error");
      return;
    }

    setLoading(true);

    const API_BASE_URL = process.env.REACT_APP_API_URL;

    try {
      const response = await fetch(
        `${API_BASE_URL}CampusActivity/GetSlotInvoiceData?id=${rowItem.invoiceId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error opening the invoice:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (selectedRow) => {
    setSelectedActivityId(selectedRow.id);
    setSelectedActivityRow(selectedRow);

    // Map group names to their respective API endpoints
    const apiEndpoints = {
      SchoolClub: `CampusActivity/GetActivitySchoolClubById?id=${selectedRow.id}`,
      Tour: `CampusActivity/GetActivityTourById?id=${selectedRow.id}`,
      Event: `CampusActivity/GetActivityEventById?id=${selectedRow.id}`,
      FacilityRental: `CampusActivity/GetActivityFacilityRentalById?id=${selectedRow.id}`,
      AfterSchoolClub: `CampusActivity/GetActivityAfterSchoolClubById?id=${selectedRow.id}`,
    };

    // Get the API endpoint based on the group name
    const endpoint = apiEndpoints[selectedRow.group];

    if (!endpoint) {
      console.error(`No API endpoint defined for group: ${selectedRow.group}`);
      toast.error(`Invalid activity type: ${selectedRow.group}`);
      return;
    }

    try {
      const response = await get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const activity = response;

      // Determine slots based on group type
      let slots = [];
      if (selectedRow.group === "Tour") {
        // For Tour, create a single-object array with tourDate, departureTime, returnTime
        slots = [
          {
            tourDate: activity.tourDate || null,
            departureTime: activity.departureTime || null,
            returnTime: activity.returnTime || null,
          },
        ];
      } else if (
        selectedRow.group === "Event" ||
        selectedRow.group === "FacilityRental"
      ) {
        // For Event and FacilityRental, use campusActivityEventDatesDtos
        slots = activity.campusActivityEventDatesDtos || [];
      } else {
        // For SchoolClub and AfterSchoolClub, use campusActivitySlotsDtos
        slots = activity.campusActivitySlotsDtos || [];
      }

      setDayAndTimeSlotsData(slots);
      setActivityDetails(activity);
      setIsEnrollModalOpen(true);
    } catch (error) {
      console.error(`Error fetching ${selectedRow.group} data:`, error);
      toast.error(`Failed to load ${selectedRow.group} details`);
    }
  };

  const handleCloseEnrollModal = () => {
    setIsEnrollModalOpen(false);
    setSelectedActivityId(null);
  };

  const handlePrintCertificate = async (studentId) => {
    if (!studentId) {
      toast.error("Error: Student ID is missing.");
      return;
    }

    setLoading(true);

    const API_BASE_URL = process.env.REACT_APP_API_URL;

    try {
      const response = await fetch(
        `${API_BASE_URL}CampusActivity/GetActivityCertificate?id=${studentId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error opening the certificate:", error);
      toast.error("Failed to print certificate.");
    } finally {
      setLoading(false);
    }
  };

  const PERMISSIONS = {
    PARENT: {
      roleIds: ["9", "10"],
      actions: [
        {
          name: "register",
          icon: "fas fa-user-plus",
          handler: "handleEnroll",
          tooltip: "Enroll",
          visible: (row) => !row.invoiceNumber,
        },
        {
          name: "printChallan",
          icon: "fas fa-print",
          handler: "handlePrintActivityChallanAction",
          tooltip: "Print Challan",
          visible: (row) => row.invoiceNumber,
          disabled: (row) => !row.isAchieved,
        },
        {
          name: "uploadPaymentProof",
          icon: "fas fa-image",
          handler: "handleUploadPaymentProofClick",
          tooltip: "Payment Proof",
          visible: (row) => row.invoiceNumber,
          disabled: (row) =>
            !row.isAchieved ||
            row.status === "Paid" ||
            row.isAccountInvoiceChallanVerified === true ||
            verifiedProofs[row.id],
        },
        {
          name: "printCertificate",
          icon: "fas fa-print",
          handler: "handlePrintCertificate",
          tooltip: "Print Certificate",
          visible: (row) => row.isCertificateGenerated,
          disabled: () => false,
        },
      ],
    },
    ACCOUNTS: {
      roleIds: ["19"],
      actions: [
        {
          name: "printChallan",
          icon: "fas fa-print",
          handler: "handlePrintActivityChallanAction",
          tooltip: "Print Challan",
          disabled: (row) => !row.isAchieved,
        },
        {
          name: "uploadPaymentProof",
          icon: "fas fa-image",
          handler: "handleUploadPaymentProofClick",
          tooltip: "Payment Proof",
          disabled: (row) => !row.isAchieved || row.status === "Paid",
        },
        {
          name: "verifyProof",
          icon: "fas fa-check-double",
          handler: "handleVerifyPaymentProofClick",
          tooltip: "Verify Proof",
          disabled: (row) =>
            !row.isAchieved ||
            row.isAccountInvoiceChallanVerified ||
            row.receiptName === null,
        },
        {
          name: "payChallan",
          icon: "fas fa-file-invoice-dollar",
          handler: "handlePayActivityChallanClick",
          tooltip: "Pay Challan",
          disabled: (row) =>
            !row.isAchieved ||
            row.status === "Paid" ||
            row.isAccountInvoiceChallanVerified === null,
        },
      ],
    },
  };

  const getActionsForRole = (roleId) => {
    const roleConfig = Object.values(PERMISSIONS).find((role) =>
      role.roleIds.includes(String(roleId))
    );
    return roleConfig ? roleConfig.actions : [];
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "pupileName",
        header: "Pupil Name",
        // size: 150,
      },
      {
        accessorKey: "activityName",
        header: "Activity",
        // size: 150,
      },
      {
        accessorKey: "group",
        header: "Activity Type",
        // size: 150,
      },
      {
        accessorKey: "fee",
        header: "Fee Amount",
        // size: 150,
      },
      {
        accessorKey: "status",
        header: "Status",
        // size: 150,
        Cell: ({ cell }) => (
          cell.getValue() ? (
            <span
              className={`badge fs-10 ${
                cell.getValue() === "Paid" ? "bg-success" : "bg-warning"
              }`}
            >
              {cell.getValue()}
            </span>
          ) : (
            "N/A"
          )
        ),
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
            const actions = getActionsForRole(roleId);
            const handlers = {
                handleEnroll,
                handlePrintActivityChallanAction,
                handleUploadPaymentProofClick,
                handleVerifyPaymentProofClick,
                handlePayActivityChallanClick,
                handlePrintCertificate,
            };

            return (
                <div className="d-flex gap-2">
                  {actions.map((action) => {
                    if (action.visible && !action.visible(row.original)) {
                      return null;
                    }
                    const isDisabled = action.disabled && action.disabled(row.original);
                    
                    return (
                      <button
                        key={action.name}
                        type="button"
                        className="btn p-0 border-0 custom-disabled-btn"
                        data-tooltip-id="global-tooltip"
                        data-tooltip-content={action.tooltip}
                        onClick={() =>
                          handlers[action.handler](
                            action.name === "verifyProof"
                              ? row.original.id
                              : action.name === "printCertificate"
                              ? row.original.studentId
                              : row.original
                          )
                        }
                        disabled={isDisabled}
                      >
                        <i className={action.icon}></i>
                      </button>
                    );
                  })}
                </div>
            );
        },
      },
    ],
    [roleId, activityApplicationsData, verifiedProofs]
  );

  const exportToExcel = () => {
    const filteredData = activityApplicationsData.map((row, index) => ({
      "Sr No": index + 1,
      "Pupil Name": row.pupileName,
      Activity: row.activityName,
      "Activity Type": row.group,
      "Fee Amount": row.fee,
      Status: row.status ? row.status : "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "EnrolledActivites");
    XLSX.writeFile(workbook, "EnrolledActivities.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Enrolled Activities List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = activityApplicationsData.map((row, index) => {
      return [
        index + 1,
        row.pupileName,
        row.activityName,
        row.group,
        row.fee,
        row.status ? row.status : "N/A",
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("EnrolledActivitiesList.pdf");
  };

  const handlePayActivityChallanClick = async (row) => {
    setPayActivityModalData(row);
    setShowPayActivityModal(true);
  };

  const submitPayActivityChallan = async (formData) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    // YEHI SABSE ZAROORI HAI — studentFirstRegistrationId ko row se lo, formData se nahi!
    const studentFirstRegistrationId =
      payActivityModalData?.studentFirstRegistrationId ||
      payActivityModalData?.pupilFirstRegistrationId ||
      payActivityModalData?.applicationId ||
      formData.studentFirstRegistrationId;

    if (!studentFirstRegistrationId) {
      toast.error("Student ID not found. Cannot process payment.");
      return;
    }

    const currentDate = new Date().toISOString();

    const payload = {
      ...formData,
      id: formData.id || payActivityModalData?.invoiceId,
      voucherNo: formData.voucherNo,
      bankName: formData.bankName,
      accountNo: formData.accountNo,
      receiptNo: formData.receiptNo,
      remarks: formData.remarks || "",

      // Ye fields backend ko chahiye hain
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      paidBy: localStorage.getItem("userId"),
      isPaid: true,
      paymentDate: currentDate,

      // YEHI FIELD ZAROORI HAI → aur ab 100% defined hai!
      studentFirstRegistrationId: Number(studentFirstRegistrationId), // ← Number mein convert kar do!
    };

    console.log("Final Payload Sent to API:", payload); // ← Debug ke liye

    try {
      const response = await post(
        `CampusActivity/UpdateActivityInvoicePaidAsync`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Payment recorded successfully!");
        setShowPayActivityModal(false);
        setTriggerRerender((prev) => !prev);
      } else {
        toast.error(response.message || "Payment failed.");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to record payment. Please try again.");
    }
  };
  //consoel.log();
  const handleUploadPaymentProofClick = async (row) => {
    setPaymentProofModalData(row);
    setShowPaymentProofModal(true);
  };

  const submitPaymentProof = async (formData) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await post(
        `CampusActivity/AddActivityInvoiceReceipt`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setShowPaymentProofModal(false);
        setTriggerRerender((prev) => !prev);
      }
    } catch (error) {
      console.error("Error saving category:", error);
    }
  };

  const handleVerifyPaymentProofClick = (rowId) => {
    setIsConfimationPaymentModalOpen(true);
    setSelectedRowId(rowId);
  };

  const handleVerifyPaymentConfirm = async () => {
    setIsSubmitting(true);
    try {
      const response = await post(
        `CampusActivity/VerifyAccountActivityInvoiceChallans?id=${selectedRowId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Verify Payment Response:", response); // Debug: Log API response
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setVerifiedProofs((prev) => ({ ...prev, [selectedRowId]: true })); // Update verified proofs
        setTriggerRerender((prev) => !prev);
      } else {
        toast.error(response.message || "An error occurred while verifying.");
      }
    } catch (error) {
      console.error("Error verifying payment proof:", error);
      toast.error("An error occurred while verifying payment proof.");
    } finally {
      setIsConfimationPaymentModalOpen(false);
      setSelectedRowId(null);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Activity Enrollment</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <CustomDatatableGrid
            columns={columns}
            data={activityApplicationsData}
            onExportExcel={exportToExcel}
            onExportPDF={exportToPDF}
            initialState={{
                columnPinning: { right: ["actions"] },
            }}
            getRowId={(row) => row.rowKey}
          />
        </div>
      </div>

      {isConfimationModalOpen && (
        <AskConfirmationModal
          handleClose={handleCloseModal}
          handleConfirm={handleConfirmDelete}
          message="Are you sure you want to delete this record?"
        />
      )}

      {showPayActivityModal && (
        <PayActivityChallanModal
          title="Pay Activity Challan"
          onClose={() => setShowPayActivityModal(false)}
          modalData={payActivityModalData}
          payActivityChallan={submitPayActivityChallan}
        />
      )}

      {showPaymentProofModal && (
        <PaymentProofModal
          title="Payment Proof"
          onClose={() => setShowPaymentProofModal(false)}
          modalData={paymentProofModalData}
          submitData={submitPaymentProof}
        />
      )}

      {isConfimationPaymentModalOpen && (
        <AskConfirmationModal
          handleClose={handleCloseModal}
          handleConfirm={handleVerifyPaymentConfirm}
          isSubmitting={isSubmitting}
          message="Are you sure you want to verify this proof?"
        />
      )}

      {isEnrollModalOpen && (
        <PupilActivityEnrollmentModal
          handleClose={handleCloseEnrollModal}
          rowData={selectedActivityRow}
          selectedActivityId={selectedActivityId}
          selectedActivityType={selectedActivityRow.group}
          activitiesData={schoolClubActivitiesData}
          nameField="clubName"
          slots={dayAndTimeSlotsData}
          activityDetails={activityDetails}
          refreshOnSubmitSuccess={getActivityApplicationsList}
        />
      )}

      {loading && <FullScreenLoader />}
    </>
  );
}

export default EnrolledActivities;
